import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { OtpService } from '@/services/otp.service';
import { AuthService } from '@/services/auth.service';
import { sendLoginMessage, sendLogoutMessage } from '@/utils/nativeBridge';
import { clearMemberSession, startMemberSession } from '@/utils/memberSession';
import { setCredentials, logout as logoutAction } from '@/store/authSlice';
import type { LoginCredentials } from '@/types/auth';

type User = {
  id: string;
  name: string;
} | null;

type OtpMode = 'email' | 'sms' | 'whatsapp';

export const useAuth = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Request OTP for login
   * @param send_to - Email or phone number (with country code)
   * @param mode - OTP delivery mode (email, sms, whatsapp)
   */
  const requestOtp = async (send_to: string, mode: OtpMode): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await OtpService.createLoginOtp({ send_to, mode });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send OTP';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Verify OTP and login
   * @param otp - The OTP code entered by user
   * @param send_to - Email or phone number where OTP was sent
   * @param mode - OTP delivery mode
   * @param recaptchaToken - ReCAPTCHA token for verification
   */
  const verifyOtpAndLogin = async (
    otp: string,
    send_to: string,
    mode: OtpMode,
    recaptchaToken: string
  ): Promise<{ token: string; user: any }> => {
    setLoading(true);
    setError(null);
    try {
      const credentials: LoginCredentials = {
        token: otp,
        recaptchaToken,
        send_to,
        mode,
      };

      const response = await AuthService.login(credentials);

      // A different member than last time on this browser: drop their shop
      // location, cart and the rest before anything reads them.
      startMemberSession(response.user.id);

      // Store token and user in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Persist first name for personalised welcome — survives logout
      if (response.user?.firstName) {
        localStorage.setItem('welcomeName', response.user.firstName);
      }

      // Update Redux store
      dispatch(setCredentials({ token: response.token, member: response.user }));

      // Fetch location now that the token is stored and axios will send it
      await AuthService.fetchUserLocation();

      // Identify member in PostHog
      const locationStr = localStorage.getItem('userLocation');
      const locationName = locationStr ? JSON.parse(locationStr)?.name : undefined;
      posthog.identify(response.user.id, {
        email: response.user.email,
        name: `${response.user.firstName} ${response.user.lastName}`,
        ...(locationName && { location: locationName }),
      });

      // Send login message to React Native WebView for push notification registration
      sendLoginMessage(response.token, response.user);
      
      setIsAuthenticated(true);
      setUser(response.user);
      return response;
    } catch (err: any) {
      const errorMessage = err.message || 'Invalid credentials';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      // 1. Send logout message to React Native WebView FIRST (while token is still valid)
      sendLogoutMessage();
      
      // 2. Wait a moment for native app to process the message with valid auth data
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 3. Then call backend logout (invalidates token)
      await AuthService.logout();
      
      // 4. Clear everything that belongs to this member
      clearMemberSession();

      // 5. Clear React Query cache so stale location data isn't served after re-login
      queryClient.removeQueries({ queryKey: ['user-location'] });

      // 6. Reset PostHog identity so the next login starts a fresh session
      posthog.reset();

      // 7. Update Redux store
      dispatch(logoutAction());

      setIsAuthenticated(false);
      setUser(null);
      setError(null);
    } catch (err: any) {
      console.error('Logout error:', err);
      // Send logout message even if logout fails (but token might already be invalid)
      sendLogoutMessage();

      // Wait a moment for processing
      await new Promise(resolve => setTimeout(resolve, 100));

      // Clear state and member storage even if logout fails
      clearMemberSession();

      // Clear React Query cache
      queryClient.removeQueries({ queryKey: ['user-location'] });

      // Reset PostHog identity
      posthog.reset();

      // Update Redux store
      dispatch(logoutAction());

      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  return {
    isAuthenticated,
    user,
    loading,
    error,
    setError,
    requestOtp,
    verifyOtpAndLogin,
    logout,
  };
};
